import type { Child, Guardian } from '@tadpoles/common'

import { deduplicateBy } from '../utils/deduplicate-by.js'
import type { FamilyUserLocalBacking } from './persistence/family-local-backing.js'
import { replacePersistedCollection } from './persistence/replace-persisted-collection.js'
import type { ServiceLifecycle } from './service-lifecycle.js'
import { UserScopedService, type UserScopedServiceScope } from './user-scoped-service-scope.js'

export class GuardianService extends UserScopedService implements ServiceLifecycle {
    readonly #localBacking: FamilyUserLocalBacking
    readonly #restoreFromBacking: boolean
    #guardians = $state.raw<Guardian[]>([])

    public constructor(
        scope: UserScopedServiceScope,
        localBacking: FamilyUserLocalBacking,
        restoreFromBacking = true
    ) {
        super(scope)
        this.#localBacking = localBacking
        this.#restoreFromBacking = restoreFromBacking
    }

    public get guardians(): Guardian[] {
        const person = this.userContext.person

        return this.#guardians.map((guardian) => {
            if (!this.#isCurrentUserGuardian(guardian, person.key)) {
                return guardian
            }

            return {
                ...guardian,
                ...(person.displayName ? { displayName: person.displayName } : {}),
                ...(person.email ? { email: person.email } : {}),
                ...(person.language ? { language: person.language } : {}),
                ...(Object.hasOwn(person, 'mobileNumber')
                    ? { mobileNumber: person.mobileNumber }
                    : {}),
                ...(person.workNumber ? { workNumber: person.workNumber } : {}),
                ...(person.homeNumber ? { homeNumber: person.homeNumber } : {})
            }
        })
    }

    public async initialize(): Promise<void> {
        if (!this.#restoreFromBacking) {
            return
        }

        const store = await this.#localBacking.getGuardianStore()
        this.hydrate(store === undefined ? [] : await store.findAll())
    }

    public resume(): void {}

    public suspend(): void {}

    public destroy(): void {
        this.hydrate([])
    }

    public hydrate(guardians: Guardian[]): void {
        this.#guardians = deduplicateBy(guardians, (guardian) => guardian.key)
    }

    public replaceAll(guardians: Guardian[]): Promise<void> {
        this.hydrate(guardians)
        const snapshot = this.guardians

        return this.enqueueCacheWrite(async () => {
            const store = await this.#localBacking.getGuardianStore()

            if (store !== undefined) {
                await replacePersistedCollection(store, snapshot, (guardian) => guardian.key)
            }
        })
    }

    public getForChild(child: Child): Guardian[] {
        return (child.guardians ?? []).flatMap(({ key }) => {
            const guardian = this.get(key)
            return guardian === null ? [] : [guardian]
        })
    }

    public canViewPhoneNumber(guardian: Guardian): boolean {
        const personKey = this.userContext.person.key
        const currentUserGuardians = this.guardians.filter((candidate) =>
            this.#isCurrentUserGuardian(candidate, personKey)
        )
        const isPeerGuardianContactAccessRestricted =
            currentUserGuardians.length === 0 ||
            currentUserGuardians.some((candidate) => candidate.restrictAccessToPeerGuardianContacts)

        if (!isPeerGuardianContactAccessRestricted) {
            return true
        }

        return this.#isCurrentUserGuardian(guardian, personKey)
    }

    public get(guardianKey: string): Guardian | null {
        return this.guardians.find((guardian) => guardian.key === guardianKey) ?? null
    }

    #isCurrentUserGuardian(guardian: Guardian, personKey: string): boolean {
        return guardian.actualPersonKey === personKey
    }
}
